import { Box, Button, Group, Modal, Stack } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { useBlocker } from '@tanstack/react-router'
import { useEffect } from 'react'
import { Titlebar } from '../Titlebar'
import { AppModalProps } from './types'

export function AppModal({
  opened,
  onClose,
  title,
  children,
  cancelProps,
  confirmProps,
  deleteProps,
  size = 'lg',
  fullScreenOnMobile = true,
  showFooter = true,
  closeOnEscape = false,
  closeOnClickOutside = false,
  withCloseButton,
  withGradient = true,
  handleBackButton = true,
  padding = 'md',
}: AppModalProps) {
  const isMobile = useMediaQuery('(max-width: 768px)')
  const shouldBeFullScreen = fullScreenOnMobile && isMobile
  const showHeader = Boolean(title)

  // Handle hardware back button on mobile fullscreen modals
  const { status, reset } = useBlocker({
    shouldBlockFn: () => shouldBeFullScreen && opened && handleBackButton,
    withResolver: true,
    enableBeforeUnload: false,
  })

  useEffect(() => {
    if (status === 'blocked') {
      onClose()
      reset()
    }
  }, [status, onClose, reset])

  // Determine if close button should be shown
  const showCloseButton =
    withCloseButton !== undefined ? withCloseButton : !isMobile // Show on desktop by default, hide on mobile

  const handleCancel = cancelProps?.onClick || onClose

  // Footer with action buttons
  const renderFooter = () => {
    if (!showFooter || (!cancelProps && !confirmProps && !deleteProps))
      return null

    return (
      <Group justify="space-between" mt="lg">
        {/* Delete button on the left */}
        {deleteProps ? (
          <Button
            variant="subtle"
            color="red"
            onClick={deleteProps.onClick}
            loading={deleteProps.loading}
            disabled={deleteProps.disabled}
          >
            {deleteProps.label || 'Poista'}
          </Button>
        ) : (
          <div /> // Empty div to maintain spacing when no delete button
        )}

        {/* Cancel and Confirm buttons on the right */}
        <Group>
          {(cancelProps !== undefined || confirmProps !== undefined) && (
            <>
              <Button
                variant="subtle"
                onClick={handleCancel}
                disabled={cancelProps?.disabled}
              >
                {cancelProps?.label || 'Peruuta'}
              </Button>

              {confirmProps && (
                <Button
                  onClick={confirmProps.onClick}
                  loading={confirmProps.loading}
                  disabled={confirmProps.disabled}
                >
                  {confirmProps.label || 'OK'}
                </Button>
              )}
            </>
          )}
        </Group>
      </Group>
    )
  }

  // For mobile fullscreen, render custom layout with Titlebar
  if (shouldBeFullScreen) {
    const hasFooter = showFooter && (cancelProps || confirmProps || deleteProps)

    return (
      <Modal
        opened={opened}
        onClose={onClose}
        fullScreen
        withCloseButton={false}
        closeOnEscape={closeOnEscape}
        closeOnClickOutside={false}
        transitionProps={{
          transition: 'slide-up',
          duration: 300,
        }}
        padding={0}
      >
        <Box
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {showHeader && (
            <Titlebar title={title} onBack={onClose} showBackButton={true} />
          )}
          <Box
            className={withGradient ? 'gradient-bg' : undefined}
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              padding:
                padding === '0' ? undefined : 'var(--mantine-spacing-md)',
            }}
          >
            {children}
          </Box>
          {hasFooter && (
            <Box
              style={{
                position: 'sticky',
                bottom: 0,
                zIndex: 1,
                paddingTop: 'var(--mantine-spacing-md)',
                paddingLeft: 'var(--mantine-spacing-md)',
                paddingRight: 'var(--mantine-spacing-md)',
                // We always add 48px to ensure keyboard doesn't cover the buttons
                paddingBottom: 'calc(var(--mantine-spacing-md) + 48px)',
                // paddingBottom:
                //  'max(var(--mantine-spacing-md), env(safe-area-inset-bottom))',
                backgroundColor: 'var(--mantine-color-body)',
                borderTop: '1px solid var(--mantine-color-default-border)',
              }}
            >
              <Group justify="space-between">
                {/* Delete button on the left */}
                {deleteProps ? (
                  <Button
                    variant="subtle"
                    color="red"
                    onClick={deleteProps.onClick}
                    loading={deleteProps.loading}
                    disabled={deleteProps.disabled}
                  >
                    {deleteProps.label || 'Poista'}
                  </Button>
                ) : (
                  <div />
                )}

                {/* Cancel and Confirm buttons on the right */}
                <Group>
                  <Button
                    variant="subtle"
                    onClick={handleCancel}
                    disabled={cancelProps?.disabled}
                  >
                    {cancelProps?.label || 'Peruuta'}
                  </Button>

                  {confirmProps && (
                    <Button
                      onClick={confirmProps.onClick}
                      loading={confirmProps.loading}
                      disabled={confirmProps.disabled}
                    >
                      {confirmProps.label || 'OK'}
                    </Button>
                  )}
                </Group>
              </Group>
            </Box>
          )}
        </Box>
      </Modal>
    )
  }

  // Desktop modal - keep existing behavior
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={showHeader ? title : undefined}
      size={size}
      centered
      withCloseButton={showCloseButton}
      closeOnEscape={closeOnEscape}
      closeOnClickOutside={closeOnClickOutside}
      transitionProps={{
        transition: 'fade',
        duration: 200,
      }}
      styles={{
        title: {
          fontWeight: 500,
          fontSize: 'var(--mantine-font-size-lg)',
        },
      }}
    >
      <Stack gap="lg" className={withGradient ? 'gradient-bg' : undefined}>
        {children}
        {renderFooter()}
      </Stack>
    </Modal>
  )
}

// Export types for convenience
export type { AppModalProps } from './types'
